import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { listAccounts } from '../accounts/api';
import type { Account } from '../../types/account';
import type { AudiencePreviewResult, AudienceRecipient } from '../../types/broadcast';

// Stage 6A v2 §6 — recipient preview modal. Full resolved audience in a
// sortable, filterable table. Data-source-agnostic (Phase 6B B+): the caller
// supplies the query key + fetcher, so BOTH broadcasts and surveys reuse it.
// `onSendNow` is optional — omit it for a read-only preview (surveys send from
// their own tab); `subtitle` adds context (e.g. the per-location note).

type RecipientPreviewModalProps = {
  /** React Query key for the details fetch (keep stable per scope). */
  queryKey: unknown[];
  /** Resolve the full recipient list (details=true preview). */
  fetchDetails: () => Promise<AudiencePreviewResult>;
  onClose: () => void;
  /** When provided, a "Send now" button is shown; omit for a read-only preview. */
  onSendNow?: () => void;
  /** Optional sub-header note (e.g. per_location clarification). */
  subtitle?: ReactNode;
};

type SortKey = 'name' | 'email' | 'phone' | 'jobTitle' | 'accounts';

function reachLabel(r: AudienceRecipient): string {
  const parts = [r.emailReachable && 'Email', r.smsReachable && 'SMS'].filter(Boolean);
  return parts.length ? parts.join(' + ') : '—';
}

// Ancestor chain (self → root) of an account id.
function ancestorChain(id: string, byId: Map<string, Account>): string[] {
  const out: string[] = [];
  let cur: Account | undefined = byId.get(id);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.push(cur.id);
    cur = cur.parentAccountId ? byId.get(cur.parentAccountId) : undefined;
  }
  return out;
}

export function RecipientPreviewModal({ queryKey, fetchDetails, onClose, onSendNow, subtitle }: RecipientPreviewModalProps) {
  const audience = useQuery({
    queryKey,
    queryFn: () => fetchDetails(),
  });
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);

  const byId = useMemo(
    () => new Map((accountsQuery.data ?? []).map((a) => [a.id, a])),
    [accountsQuery.data],
  );
  const recipients = audience.data?.recipients ?? [];

  // Ancestor chains + root brand per audience account (for the filter matching).
  const chains = useMemo(() => {
    const m = new Map<string, string[]>();
    const audienceAccountIds = new Set(recipients.flatMap((r) => r.accountIds));
    for (const id of audienceAccountIds) m.set(id, ancestorChain(id, byId));
    return m;
  }, [recipients, byId]);

  const rootBrandOf = (accountId: string): string | null => {
    const chain = chains.get(accountId) ?? [];
    const brand = chain.find((id) => byId.get(id)?.accountType === 'brand');
    return brand ?? null;
  };

  // Filter dropdown options derived from the resolved audience accounts.
  const { brandOpts, groupOpts, accountOpts } = useMemo(() => {
    const audienceAccountIds = new Set(recipients.flatMap((r) => r.accountIds));
    const brands = new Set<string>();
    const groups = new Set<string>();
    for (const id of audienceAccountIds) {
      for (const anc of chains.get(id) ?? []) {
        const t = byId.get(anc)?.accountType;
        if (t === 'brand') brands.add(anc);
        else if (t === 'group') groups.add(anc);
      }
    }
    const opt = (ids: Iterable<string>) =>
      [...ids]
        .map((id) => ({ id, name: byId.get(id)?.name ?? id }))
        .sort((a, b) => a.name.localeCompare(b.name));
    return {
      brandOpts: opt(brands),
      groupOpts: opt(groups),
      accountOpts: opt(audienceAccountIds),
    };
  }, [recipients, chains, byId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = recipients.filter((r) => {
      if (q) {
        const hay = `${r.contactName} ${r.email ?? ''} ${r.phone ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (brandFilter && !r.accountIds.some((id) => rootBrandOf(id) === brandFilter)) return false;
      if (groupFilter && !r.accountIds.some((id) => (chains.get(id) ?? []).includes(groupFilter)))
        return false;
      if (accountFilter && !r.accountIds.includes(accountFilter)) return false;
      return true;
    });
    const dir = sortAsc ? 1 : -1;
    const key = (r: AudienceRecipient): string => {
      switch (sortKey) {
        case 'email':
          return r.email ?? '';
        case 'phone':
          return r.phone ?? '';
        case 'jobTitle':
          return r.jobTitle ?? '';
        case 'accounts':
          return r.accountNames.join(', ');
        default:
          return r.contactName;
      }
    };
    return [...rows].sort((a, b) => key(a).localeCompare(key(b)) * dir);
  }, [recipients, search, brandFilter, groupFilter, accountFilter, sortKey, sortAsc, chains]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const total = audience.data?.totalContacts ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col rounded-md border border-slate-200 bg-white shadow-xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">Audience preview</h2>
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            ✕
          </button>
        </header>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email / phone…"
            className="min-w-48 flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          />
          <FilterSelect label="Brand" value={brandFilter} onChange={setBrandFilter} options={brandOpts} />
          <FilterSelect label="Group" value={groupFilter} onChange={setGroupFilter} options={groupOpts} />
          <FilterSelect label="Account" value={accountFilter} onChange={setAccountFilter} options={accountOpts} />
        </div>

        <div className="flex-1 overflow-auto">
          {audience.isLoading ? (
            <CenteredSpinner />
          ) : audience.error ? (
            <p className="p-5 text-sm text-red-600">Couldn't resolve the audience.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableTh label="Name" k="name" sortKey={sortKey} asc={sortAsc} onSort={onSort} />
                  <SortableTh label="Email" k="email" sortKey={sortKey} asc={sortAsc} onSort={onSort} />
                  <SortableTh label="Phone" k="phone" sortKey={sortKey} asc={sortAsc} onSort={onSort} />
                  <SortableTh label="Job title" k="jobTitle" sortKey={sortKey} asc={sortAsc} onSort={onSort} />
                  <SortableTh label="Accounts" k="accounts" sortKey={sortKey} asc={sortAsc} onSort={onSort} />
                  <th className="px-3 py-2 font-medium">Channels</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-slate-400">
                      No recipients match.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.contactId} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-900">{r.contactName}</td>
                      <td className="px-3 py-2 text-slate-600">{r.email ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.phone ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.jobTitle ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.accountNames.join(', ') || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{reachLabel(r)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-sm text-slate-500">
            Showing {filtered.length} of {total} recipients
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {onSendNow && (
              <Button onClick={onSendNow} disabled={total === 0}>
                Send now
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ id: string; name: string }>;
}) {
  return (
    <SelectField
      size="sm"
      aria-label={label}
      value={value}
      onValueChange={onChange}
      options={[
        { value: '', label: `All ${label.toLowerCase()}s` },
        ...options.map((o) => ({ value: o.id, label: o.name })),
      ]}
    />
  );
}

function SortableTh({
  label,
  k,
  sortKey,
  asc,
  onSort,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  asc: boolean;
  onSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th className="px-3 py-2 font-medium">
      <button type="button" onClick={() => onSort(k)} className="inline-flex items-center gap-1 hover:text-slate-900">
        {label}
        {active && <span>{asc ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
